import { useAppDispatch, useAppSelector } from "@app/store";
import { useCallback } from "react";
import {
  ActivateUserRequest,
  AddMerchantToPlatformRequest,
  AddMerchantWalletsRequest,
  AddTransactionToPlatformRequest,
  AdminUsersRequest,
  ChangePasswordRequest,
  EditMerchantToPlatformRequest,
  resetUserTOTPRequest,
  SetPlatformTeamleadComissionRequest,
  SetUserRoleRequest,
  UpdateUserNotificationSettingsRequest,
  UserChangeBalanceRequest,
  UserInfoUpdateRequest,
} from "./api";
import {
  activateUser,
  addMerchantToPlatform,
  addMerchantWallets,
  addTransactionToPlatform,
  changeUserBalance,
  changeUserPassword,
  editMerchantToPlatform,
  getAdminUserInfo,
  getAdminUserRoles,
  getAdminUsers,
  getApiKey,
  getPlatformTransactions,
  resetUserTOTP,
  setPlatformTeamleadComission,
  setUserRole,
  updateAdminUserInfo,
  updateUserNotificationSettings,
} from "./slice";

export const useAdminUsers = () => {
  const state = useAppSelector((state) => state.adminUsers);
  const dispatch = useAppDispatch();

  const getAdminUsersHandler = useCallback(
    (data?: AdminUsersRequest) => dispatch(getAdminUsers({ data })),
    [dispatch]
  );

  const getAdminUserInfoHandler = useCallback(
    (userId: string) => dispatch(getAdminUserInfo({ userId })),
    [dispatch]
  );

  const updateAdminUserInfoHandler = useCallback(
    (userId: string, data: UserInfoUpdateRequest) =>
      dispatch(updateAdminUserInfo({ userId, data })),
    [dispatch]
  );

  const changeUserBalanceHandler = useCallback(
    (balanceId: string, data: UserChangeBalanceRequest) =>
      dispatch(changeUserBalance({ balanceId, data })),
    [dispatch]
  );

  const changeUserPasswordHandler = useCallback(
    (data: ChangePasswordRequest) => dispatch(changeUserPassword({ data })),
    [dispatch]
  );

  const resetUserTOTPHandler = useCallback(
    (data: resetUserTOTPRequest) => dispatch(resetUserTOTP({ data })),
    [dispatch]
  );

  const getAdminUserRolesHandler = useCallback(
    () => dispatch(getAdminUserRoles()),
    [dispatch]
  );

  const addMerchantWalletsHandler = useCallback(
    (data: AddMerchantWalletsRequest) => dispatch(addMerchantWallets({ data })),
    [dispatch]
  );

  const activateUserHandler = useCallback(
    (data: ActivateUserRequest) => dispatch(activateUser({ data })),
    [dispatch]
  );

  const updateUserNotificationSettingsHandler = useCallback(
    (userId: string, data: UpdateUserNotificationSettingsRequest) =>
      dispatch(updateUserNotificationSettings({ userId, data })),
    [dispatch]
  );

  const setUserRoleHandler = useCallback(
    (userId: string, data: SetUserRoleRequest) =>
      dispatch(setUserRole({ userId, data })),
    [dispatch]
  );

  const getApiKeyHandler = useCallback(
    (userId: string) => dispatch(getApiKey({ userId })),
    [dispatch]
  );

  const getPlatformTransactionsHandler = useCallback(
    (userId: string) => dispatch(getPlatformTransactions({ userId })),
    [dispatch]
  );

  const addMerchantToPlatformHandler = useCallback(
    (userId: string, data: AddMerchantToPlatformRequest) =>
      dispatch(addMerchantToPlatform({ userId, data })),
    [dispatch]
  );

  const editMerchantToPlatformHandler = useCallback(
    (userId: string, data: EditMerchantToPlatformRequest) =>
      dispatch(editMerchantToPlatform({ userId, data })),
    [dispatch]
  );

  const addTransactionToPlatformHandler = useCallback(
    (userId: string, data: AddTransactionToPlatformRequest) =>
      dispatch(addTransactionToPlatform({ userId, data })),
    [dispatch]
  );

  const setPlatformTeamleadComissionHandler = useCallback(
    (userId: string, data: SetPlatformTeamleadComissionRequest) =>
      dispatch(setPlatformTeamleadComission({ userId, data })),
    [dispatch]
  );

  return {
    state,
    getAdminUsers: getAdminUsersHandler,
    getAdminUserInfo: getAdminUserInfoHandler,
    updateAdminUserInfo: updateAdminUserInfoHandler,
    changeUserBalance: changeUserBalanceHandler,
    changeUserPassword: changeUserPasswordHandler,
    resetUserTOTP: resetUserTOTPHandler,
    getAdminUserRoles: getAdminUserRolesHandler,
    addMerchantWallets: addMerchantWalletsHandler,
    activateUser: activateUserHandler,
    updateUserNotificationSettings: updateUserNotificationSettingsHandler,
    setUserRole: setUserRoleHandler,
    getApiKey: getApiKeyHandler,
    getPlatformTransactions: getPlatformTransactionsHandler,
    addMerchantToPlatform: addMerchantToPlatformHandler,
    editMerchantToPlatform: editMerchantToPlatformHandler,
    addTransactionToPlatform: addTransactionToPlatformHandler,
    setPlatformTeamleadComission: setPlatformTeamleadComissionHandler,
  };
};
